import {
  createInscription,
  fetchCohortes,
  fetchDossierStatut,
  fetchInscription,
  fetchMyInscriptions,
} from '../services/academiaApprenantService';
import { isCohorteFull } from './cohorteCapacityMessage';
import {
  buildInscriptionPaymentNavigationState,
  inscriptionFraisInscriptionDue,
} from './inscriptionPresentation';

function isOpenCohorte(c) {
  const statut = String(c.statut ?? '').toUpperCase();
  return (statut === 'OUVERTE' || statut === 'EN_COURS') && !isCohorteFull(c);
}

function isPendingPayment(ins) {
  const statut = ins?.statut;
  if (statut === 'ANNULEE' || statut === 'DESISTEE' || statut === 'BROUILLON') {
    return false;
  }
  return inscriptionFraisInscriptionDue(ins) > 0.01;
}

function apiErrorMessage(error, fallback) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

/**
 * Crée ou réutilise une inscription cohorte, puis calcule le montant des frais d'inscription à régler.
 * @param {string} filiereId
 * @returns {Promise<
 *   | {
 *       ok: true;
 *       inscriptionId: string;
 *       montantInscription: number;
 *       focusInscription: boolean;
 *       maxAmount: number;
 *       allowCustomAmount: boolean;
 *       paymentChoice: 'inscription' | 'total' | 'advance';
 *       filiereLabel?: string;
 *     }
 *   | { ok: false; code: string; message: string }
 * >}
 */
export async function prepareFormationSubscriptionPayment(filiereId) {
  const idFiliere = String(filiereId ?? '').trim();
  if (!idFiliere) {
    return { ok: false, code: 'API', message: 'Formation introuvable.' };
  }

  try {
    const dossier = await fetchDossierStatut();
    const eligible = dossier?.inscriptionEligible ?? dossier?.complet;
    if (!eligible) {
      return {
        ok: false,
        code: 'DOSSIER',
        message:
          dossier?.messageInscription ??
          "Déposez et faites valider votre pièce d'identité avant de souscrire.",
      };
    }

    const cohortes = await fetchCohortes(idFiliere);
    const cohortesOuvertes = (cohortes ?? []).filter(isOpenCohorte);
    if (cohortesOuvertes.length === 0) {
      return {
        ok: false,
        code: 'NO_COHORTE',
        message: 'Aucune cohorte avec des places disponibles pour cette formation.',
      };
    }

    const mes = await fetchMyInscriptions();
    let inscription = (mes ?? []).find((i) => i.idFiliere === idFiliere && isPendingPayment(i));

    if (!inscription) {
      const cohorte = cohortesOuvertes[0];
      try {
        inscription = await createInscription(cohorte.id);
      } catch (createError) {
        const msg = apiErrorMessage(createError, '');
        const retry = (mes ?? []).find(
          (i) => i.idCohorte === cohorte.id || (i.idFiliere === idFiliere && isPendingPayment(i)),
        );
        if (retry) {
          inscription = retry;
        } else if (msg.toLowerCase().includes('déjà inscrit') || msg.toLowerCase().includes('deja inscrit')) {
          const existing = (mes ?? []).find((i) => i.idCohorte === cohorte.id);
          if (existing) {
            inscription = existing;
          } else {
            return { ok: false, code: 'API', message: msg || 'Inscription impossible.' };
          }
        } else {
          return { ok: false, code: 'API', message: msg || 'Inscription impossible.' };
        }
      }
    }

    if (!inscription?.id) {
      return { ok: false, code: 'API', message: 'Impossible de préparer votre inscription.' };
    }

    if (!inscription.lignesFrais?.length) {
      inscription = await fetchInscription(inscription.id);
    }

    const paymentNav = buildInscriptionPaymentNavigationState(inscription);
    if (!paymentNav) {
      return {
        ok: false,
        code: 'ALREADY_PAID',
        message: 'Les frais de cette formation sont déjà réglés pour votre inscription en cours.',
      };
    }

    return {
      ok: true,
      inscriptionId: inscription.id,
      montantInscription: paymentNav.montant,
      focusInscription: paymentNav.focusInscription,
      maxAmount: paymentNav.maxAmount,
      allowCustomAmount: paymentNav.allowCustomAmount,
      paymentChoice: paymentNav.paymentChoice,
      filiereLabel: paymentNav.filiereLabel || inscription.filiere?.libelle,
    };
  } catch (error) {
    return {
      ok: false,
      code: 'API',
      message: apiErrorMessage(error, 'Préparation du paiement impossible.'),
    };
  }
}
